import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Layout from '@/components/Layout';
import { useRentalVehicle } from '@/hooks/useRentalVehicles';
import { VehicleBookingForm } from '@/components/VehicleBookingForm';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Luggage, Gauge, Fuel, Check, ArrowLeft } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const VehicleDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: vehicle, isLoading } = useRentalVehicle(slug || '');

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!vehicle) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-3xl font-bold mb-4">Vehicle Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The vehicle you're looking for doesn't exist or is no longer available.
          </p>
          <Link to="/car-rental">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Car Rental
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const allImages = [
    vehicle.main_image_url,
    ...(vehicle.gallery_images_urls || []),
  ].filter(Boolean);

  return (
    <Layout>
      <Helmet>
        <title>{vehicle.vehicle_name} - Car Rental</title>
        <meta name="description" content={vehicle.short_description || `Rent ${vehicle.vehicle_name} in Dominican Republic`} />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/car-rental" className="text-primary hover:underline flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Car Rental
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <div className="relative">
              {allImages.length > 0 ? (
                <Carousel className="w-full">
                  <CarouselContent>
                    {allImages.map((image, index) => (
                      <CarouselItem key={index}>
                        <div className="aspect-video rounded-lg overflow-hidden">
                          <img
                            src={image || '/placeholder.svg'}
                            alt={`${vehicle.vehicle_name} - Image ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {allImages.length > 1 && (
                    <>
                      <CarouselPrevious className="left-4" />
                      <CarouselNext className="right-4" />
                    </>
                  )}
                </Carousel>
              ) : (
                <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                  <p className="text-muted-foreground">No images available</p>
                </div>
              )}
              {vehicle.is_featured && (
                <Badge className="absolute top-4 right-4 bg-primary">Featured</Badge>
              )}
            </div>

            {/* Vehicle Info */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold mb-2">{vehicle.vehicle_name}</h1>
                  <p className="text-lg text-muted-foreground">
                    {vehicle.brand} {vehicle.model} {vehicle.year}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">From</p>
                  <p className="text-3xl font-bold text-primary">
                    ${vehicle.daily_rate}
                    <span className="text-base font-normal text-muted-foreground">/day</span>
                  </p>
                </div>
              </div>

              {/* Quick Specs */}
              <Card className="mb-6">
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="flex flex-col items-center text-center">
                      <Users className="w-8 h-8 mb-2 text-primary" />
                      <p className="text-sm text-muted-foreground">Passengers</p>
                      <p className="font-semibold">{vehicle.passengers}</p>
                    </div>
                    <div className="flex flex-col items-center text-center">
                      <Luggage className="w-8 h-8 mb-2 text-primary" />
                      <p className="text-sm text-muted-foreground">Luggage</p>
                      <p className="font-semibold">{vehicle.luggage} Bags</p>
                    </div>
                    <div className="flex flex-col items-center text-center">
                      <Gauge className="w-8 h-8 mb-2 text-primary" />
                      <p className="text-sm text-muted-foreground">Transmission</p>
                      <p className="font-semibold capitalize">{vehicle.transmission}</p>
                    </div>
                    <div className="flex flex-col items-center text-center">
                      <Fuel className="w-8 h-8 mb-2 text-primary" />
                      <p className="text-sm text-muted-foreground">Fuel Type</p>
                      <p className="font-semibold capitalize">{vehicle.fuel_type}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Description */}
              {(vehicle.description || vehicle.short_description) && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold mb-4">About This Vehicle</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-4 break-words overflow-wrap-anywhere">
                    {(vehicle.description || vehicle.short_description)?.split('\n').map((paragraph, index) => (
                      paragraph.trim() && (
                        <p key={index} className="mb-3 text-base leading-7 whitespace-pre-wrap">
                          {paragraph}
                        </p>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Features */}
              {vehicle.features && vehicle.features.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold mb-4">Features & Amenities</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {vehicle.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing Info */}
              {(vehicle.weekly_rate || vehicle.monthly_rate) && (
                <Card className="mb-8">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-4">Rental Rates</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Daily Rate</span>
                        <span className="font-semibold">${vehicle.daily_rate}/day</span>
                      </div>
                      {vehicle.weekly_rate && (
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">Weekly Rate</span>
                          <span className="font-semibold">${vehicle.weekly_rate}/week</span>
                        </div>
                      )}
                      {vehicle.monthly_rate && (
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">Monthly Rate</span>
                          <span className="font-semibold">${vehicle.monthly_rate}/month</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Right Column - Booking Form */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <VehicleBookingForm vehicle={vehicle} />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default VehicleDetail;